// frontend/src/components/common/EmailVerificationNotification.tsx
import React, { useState } from 'react';
import { Mail, X, Clock, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface EmailVerificationNotificationProps {
  className?: string;
}

const EmailVerificationNotification: React.FC<EmailVerificationNotificationProps> = ({ 
  className = '' 
}) => {
  const { 
    user, 
    emailVerificationStatus, 
    resendVerification, 
    dismissVerificationNotification 
  } = useAuth();
  
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');

  // Don't show if no verification status or email is already verified
  if (!emailVerificationStatus || emailVerificationStatus.is_verified) {
    return null;
  }

  const handleResendVerification = async () => {
    if (!user?.email) return;

    setIsResending(true);
    setResendError('');
    setResendSuccess(false);

    try {
      const result = await resendVerification(user.email);
      
      if (result.success) {
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000); // Clear success message after 5 seconds
      } else {
        setResendError(result.message || 'Failed to resend verification email');
      }
    } catch (error: any) {
      setResendError(error.message || 'An error occurred');
    } finally {
      setIsResending(false);
    }
  };

  const handleDismiss = () => {
    dismissVerificationNotification();
  };

  // Determine notification style based on urgency
  const isUrgent = emailVerificationStatus.days_remaining <= 2;
  const isExpired = emailVerificationStatus.grace_period_expired;

  const getNotificationStyles = () => {
    if (isExpired) {
      return {
        container: 'bg-red-50 border-red-200',
        icon: 'text-red-500',
        title: 'text-red-800',
        text: 'text-red-700',
        button: 'bg-red-600 hover:bg-red-700 text-white',
        iconComponent: AlertTriangle
      };
    } else if (isUrgent) {
      return {
        container: 'bg-orange-50 border-orange-200',
        icon: 'text-orange-500',
        title: 'text-orange-800',
        text: 'text-orange-700',
        button: 'bg-orange-600 hover:bg-orange-700 text-white',
        iconComponent: Clock
      };
    } else {
      return {
        container: 'bg-blue-50 border-blue-200',
        icon: 'text-blue-500',
        title: 'text-blue-800',
        text: 'text-blue-700',
        button: 'bg-blue-600 hover:bg-blue-700 text-white',
        iconComponent: Mail
      };
    }
  };

  const styles = getNotificationStyles();
  const IconComponent = styles.iconComponent;

  const getTitle = () => {
    if (isExpired) {
      return 'Email Verification Required';
    } else if (isUrgent) {
      return 'Email Verification Reminder';
    } else {
      return 'Please Verify Your Email';
    }
  };

  const getMessage = () => {
    if (isExpired) {
      return 'Your email verification grace period has expired. Please verify your email to continue using all features.';
    } else if (isUrgent) {
      return `Please verify your email address. You have ${emailVerificationStatus.days_remaining} ${emailVerificationStatus.days_remaining === 1 ? 'day' : 'days'} remaining.`;
    } else {
      return `We've sent a verification email to ${user?.email}. Please verify your email within ${emailVerificationStatus.days_remaining} days to ensure uninterrupted access.`;
    }
  };

  return (
    <div className={`rounded-lg border p-4 mb-4 ${styles.container} ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <IconComponent className={`h-5 w-5 ${styles.icon}`} />
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${styles.title}`}>
            {getTitle()}
          </h3>
          <div className={`mt-2 text-sm ${styles.text}`}>
            <p>{getMessage()}</p>
            
            {resendSuccess && (
              <div className="mt-2 flex items-center text-green-700">
                <CheckCircle className="h-4 w-4 mr-1" />
                <span className="text-sm">Verification email sent successfully!</span>
              </div>
            )}
            
            {resendError && (
              <div className="mt-2 text-red-700">
                <p className="text-sm">{resendError}</p>
              </div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={handleResendVerification}
              disabled={isResending}
              className={`inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md ${styles.button} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isResending ? (
                <>
                  <RefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="-ml-1 mr-2 h-4 w-4" />
                  Resend Verification Email
                </>
              )}
            </button>
            
            {!isExpired && (
              <button
                onClick={handleDismiss}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
        
        {!isExpired && (
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={handleDismiss}
              className={`inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-500`}
            >
              <span className="sr-only">Dismiss</span>
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationNotification;